/**
 * CP-4.2 — Booking screens.
 *
 *  - Lead inbox at /app/leads/me/bookings (data-testid='bookings-root')
 *  - Lead booking detail at /app/leads/me/bookings/{id}
 *  - Client booking wizard at /app/jobs/{job_id}/book/{lead_uuid}
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { ClientBudgetCard, clientBudget } from "@/lib/budget";

const STATE_TONE = {
  requested: "avcp-pill",
  accepted: "avcp-pill ok",
  declined: "avcp-pill warn",
  cancelled_by_client: "avcp-pill warn",
};

function StatePill({ state }) {
  return <span className={STATE_TONE[state] || "avcp-pill"} data-testid={TID.bookingState}>{state}</span>;
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// ---------------------------------------------------------------------------
// LEAD INBOX
// ---------------------------------------------------------------------------

export function LeadBookingsInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api("/leads/me/bookings");
        setItems(r.data || []);
      } catch (e) { toast.error(e.message); } finally { setLoading(false); }
    })();
  }, []);
  return (
    <Layout>
      <div data-testid={TID.bookingsRoot}>
        <h1 className="text-3xl font-bold tracking-tight mb-2">Booking Requests</h1>
        <p className="text-[var(--avcp-ink-soft)] mb-6">
          Incoming booking requests on Workforce Partners. Accepting reveals the full site address, contacts, and meeting info.
        </p>
        {loading ? <div className="avcp-card p-6">Loading…</div> : (
          <ul className="space-y-3" data-testid={TID.bookingsList}>
            {items.length === 0 && <li className="avcp-card p-6 text-sm text-[var(--avcp-ink-soft)]">No bookings yet.</li>}
            {items.map((b) => (
              <li key={b.id} className="avcp-card p-5" data-testid={TID.bookingRow}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <Link to={`/app/leads/me/bookings/${b.id}`} className="font-semibold">{b.job_title}</Link>
                    <div className="text-xs text-[var(--avcp-ink-soft)] mt-1 flex items-center gap-2 flex-wrap">
                      <StatePill state={b.state} />
                      <span>{fmt(b.starts_at)} → {fmt(b.ends_at)}</span>
                      <span>· {b.region_label} · {b.service_city || "—"}</span>
                      <span>· {b.currency} {b.lead_rate_snapshot} lead / {b.helper_rate_standard_snapshot} helper</span>
                      <span>· {b.helper_count_requested} helper technicians</span>
                    </div>
                    {clientBudget(b) && <div className="mt-3"><ClientBudgetCard record={b} compact /></div>}
                    {b.client_message && <p className="text-sm mt-2">&ldquo;{b.client_message}&rdquo;</p>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// LEAD BOOKING DETAIL
// ---------------------------------------------------------------------------

export function LeadBookingDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    try {
      const r = await api(`/leads/me/bookings/${id}`);
      setData(r);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { refresh(); }, [id]);
  if (!data) return <Layout><div className="avcp-card p-6">Loading…</div></Layout>;
  const b = data.booking;
  const j = data.job;
  const accept = async () => {
    setBusy(true);
    try {
      const unlocked = await api(`/leads/me/bookings/${id}/accept`, {
        method: "POST", body: { message: msg || null },
      });
      toast.success("Accepted — address now visible");
      // CR1 — backend returns the unlocked detail payload inline; no second fetch.
      setData(unlocked);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const decline = async () => {
    if (!window.confirm("Decline this booking?")) return;
    setBusy(true);
    try {
      await api(`/leads/me/bookings/${id}/decline`, { method: "POST", body: { reason: reason || null } });
      toast.success("Declined");
      await refresh();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Layout>
      <div data-testid={TID.bookingDetailRoot}>
        <div className="flex items-center justify-between gap-2 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{j.title}</h1>
            <div className="flex items-center gap-2 mt-2 text-xs">
              <StatePill state={b.state} />
              <span>{fmt(j.starts_at)} → {fmt(j.ends_at)}</span>
              <span>· {b.currency} {b.lead_rate_snapshot}/h lead · {b.helper_rate_standard_snapshot}/h helper · {b.helper_count_requested} helper technicians</span>
            </div>
          </div>
          {b.state === "requested" && (
            <div className="flex gap-2">
              <button className="avcp-btn primary" disabled={busy} onClick={accept} data-testid={TID.bookingAccept}>Accept</button>
              <button className="avcp-btn ghost" disabled={busy} onClick={decline} data-testid={TID.bookingDecline}>Decline</button>
            </div>
          )}
          {(b.state === "accepted" || b.state === "crew_assigned") && (
            <Link to={`/app/leads/me/bookings/${id}/crew`} className="avcp-btn primary">
              Manage Crew
            </Link>
          )}
        </div>

        {clientBudget(b) && <div className="mb-4"><ClientBudgetCard record={b} /></div>}

        {b.client_po_number && (
          <div className="avcp-card p-4 mb-4 text-sm">
            <span className="text-[var(--avcp-ink-soft)]">Purchase Order:</span>
            <span className="ml-2 font-mono">{b.client_po_number}</span>
            <span className="avcp-pill ml-2">
              {b.client_po_locked_at ? "Locked" : (b.client_po_source === "client_edited" ? "Edited" : "Auto-generated")}
            </span>
          </div>
        )}

        {b.state === "requested" && (
          <div className="avcp-card p-4 mb-4 grid md:grid-cols-2 gap-2">
            <input className="avcp-input" placeholder="Acceptance message (optional)"
                   value={msg} onChange={(e) => setMsg(e.target.value)}
                   data-testid={TID.bookingAcceptMsg} />
            <input className="avcp-input" placeholder="Decline reason (optional)"
                   value={reason} onChange={(e) => setReason(e.target.value)}
                   data-testid={TID.bookingDeclineReason} />
          </div>
        )}

        <section className="avcp-card p-6 mb-4">
          <h2 className="font-semibold mb-2">Job</h2>
          <p className="text-sm whitespace-pre-line">{j.description || "—"}</p>
          <div className="grid md:grid-cols-2 gap-2 mt-3 text-sm">
            <div><span className="text-[var(--avcp-ink-soft)]">Region</span>: {j.region_id}</div>
            <div><span className="text-[var(--avcp-ink-soft)]">City</span>: {j.service_city || "—"}</div>
            <div><span className="text-[var(--avcp-ink-soft)]">Duration</span>: {j.job_duration_kind}</div>
            <div><span className="text-[var(--avcp-ink-soft)]">Helpers requested</span>: {j.helper_count_requested}</div>
          </div>
          {data.client_org && (
            <div className="mt-3 text-sm">
              <span className="text-[var(--avcp-ink-soft)]">Client</span>: {data.client_org.name}
              {data.client_org.website && <a className="ml-2 underline" target="_blank" rel="noreferrer" href={data.client_org.website}>{data.client_org.website}</a>}
            </div>
          )}
        </section>

        {data.address_revealed ? (
          <>
            <section className="avcp-card p-6 mb-4" data-testid={TID.bookingAddressBox}>
              <h2 className="font-semibold mb-2">Site address (revealed on acceptance)</h2>
              <p className="text-sm">{j.service_address}</p>
              <p className="text-sm">{j.service_city}{j.service_postal ? `, ${j.service_postal}` : ""}</p>
              <hr className="my-3 border-[var(--avcp-line)]" />
              <h3 className="font-medium mb-1">Meeting & site access</h3>
              <p className="text-sm">Meet: {j.meet_location || "—"} at {fmt(j.meet_at)}</p>
              {j.parking_instructions && <p className="text-sm">Parking: {j.parking_instructions}</p>}
              {j.site_access_notes && <p className="text-sm">Access: {j.site_access_notes}</p>}
              {j.notes_internal && (
                <>
                  <hr className="my-3 border-[var(--avcp-line)]" />
                  <h3 className="font-medium mb-1">Internal notes</h3>
                  <p className="text-sm whitespace-pre-line">{j.notes_internal}</p>
                </>
              )}
            </section>
            <section className="avcp-card p-6 mb-4" data-testid={TID.bookingContactsBox}>
              <h2 className="font-semibold mb-2">Contacts ({data.contacts.length})</h2>
              <ul className="space-y-2 text-sm">
                {data.contacts.map((c) => (
                  <li key={c.id} className="p-2 rounded bg-[var(--avcp-bg-2)]">
                    <div className="font-medium">{c.display_name} <span className="text-xs text-[var(--avcp-ink-soft)]">· {c.kind}{c.role_label ? ` · ${c.role_label}` : ""}</span></div>
                    {c.phone && <div>{c.phone}</div>}
                    {c.email && <div>{c.email}</div>}
                  </li>
                ))}
              </ul>
            </section>
            {data.packages.length > 0 && (
              <section className="avcp-card p-6">
                <h2 className="font-semibold mb-2">Packages</h2>
                <ul className="space-y-2">
                  {data.packages.map((p) => (
                    <li key={p.id} className="border border-[var(--avcp-line)] rounded p-3">
                      <div className="font-medium">{p.title} <span className="text-xs text-[var(--avcp-ink-soft)]">· {p.kind}</span></div>
                      <ul className="mt-2 text-sm space-y-1">
                        {p.versions.map((v) => (
                          <li key={v.id} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-[var(--avcp-bg-2)]">
                            <span className="font-mono text-xs">v{v.version}</span>
                            <span className="flex-1 text-xs text-[var(--avcp-ink-soft)] truncate">{fmt(v.created_at)} · {v.mime_type} · {(v.size_bytes/1024).toFixed(1)} KB</span>
                            <a className="avcp-btn ghost" href={v.download_url} target="_blank" rel="noreferrer">Download</a>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        ) : (
          <section className="avcp-card p-6 mb-4 border border-dashed border-[var(--avcp-line)]">
            <p className="text-sm text-[var(--avcp-ink-soft)]">
              Exact site address, contacts, meeting details, and package downloads will be revealed once you accept.
            </p>
          </section>
        )}
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// CLIENT BOOKING WIZARD — initiated from contractor public profile
// ---------------------------------------------------------------------------

export function BookLeadWizard() {
  const { lead_uuid } = useParams();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [helperCount, setHelperCount] = useState(0);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadProfile, setLeadProfile] = useState(null);
  const [helperRate, setHelperRate] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [j, l] = await Promise.all([
          api("/jobs?status=published"),
          api(`/contractors/${lead_uuid}`),
        ]);
        setJobs(j.data || []);
        setLeadProfile(l.profile || null);
      } catch (e) { toast.error(e.message); }
    })();
  }, [lead_uuid]);

  // Pull standard helper rate hint from admin config (best effort — currency comes from selected job)
  useEffect(() => {
    const job = jobs.find((j) => String(j.id) === String(jobId));
    if (!job || !leadProfile) { setHelperRate(null); return; }
    // We don't expose the config endpoint generally; approximate from currency:
    const guess = { USD: 35, CAD: 38, EUR: 32, GBP: 28, AUD: 40 };
    setHelperRate(guess[job.currency] || null);
  }, [jobId, jobs, leadProfile]);

  const selectedJob = jobs.find((j) => String(j.id) === String(jobId));
  const hours = useMemo(() => {
    if (!selectedJob) return 0;
    const h = (new Date(selectedJob.ends_at) - new Date(selectedJob.starts_at)) / 3600000;
    return Math.max(0, Math.round(h * 10) / 10);
  }, [selectedJob]);
  const total = useMemo(() => {
    if (!selectedJob || !leadProfile) return null;
    const leadRate = Number(leadProfile.hourly_rate) || 0;
    const hRate = helperRate || 0;
    const t = leadRate * hours + (Number(helperCount) || 0) * hRate * hours;
    return Number.isFinite(t) ? Math.round(t * 100) / 100 : null;
  }, [selectedJob, leadProfile, helperRate, hours, helperCount]);

  const submit = async () => {
    if (!jobId) { toast.error("Pick a job"); return; }
    setBusy(true);
    try {
      const r = await api(`/jobs/${jobId}/booking-request`, {
        method: "POST",
        body: {
          lead_user_uuid: lead_uuid,
          helper_count_requested: Number(helperCount) || 0,
          client_message: message || null,
        },
      });
      toast.success("Booking request sent");
      navigate(`/app/jobs/${jobId}`);
      return r;
    } catch (e) {
      toast.error(e?.problem?.detail || e?.problem?.title || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div data-testid={TID.bookWizardRoot}>
        <h1 className="text-3xl font-bold tracking-tight mb-1">Send booking request</h1>
        <p className="text-[var(--avcp-ink-soft)] mb-6">
          You hire the Lead Technician. Helper Technicians are dispatched by your Lead in a later step —
          clients never select Helpers directly.
        </p>

        {leadProfile && (
          <div className="avcp-card p-5 mb-4">
            <div className="text-sm text-[var(--avcp-ink-soft)]">Lead</div>
            <div className="font-semibold">{leadProfile.company_name || "(unnamed)"}</div>
            <div className="text-sm">Rate: <span className="font-mono">{leadProfile.currency || ""} {leadProfile.hourly_rate ?? "—"}</span> / hour</div>
            {leadProfile.operational_status === "vacation" && (
              <div className="mt-2"><span className="avcp-pill warn">On vacation — may decline</span></div>
            )}
          </div>
        )}

        <section className="avcp-card p-5 space-y-3">
          <label className="grid gap-1 text-sm">
            <span>Which published job?</span>
            <select className="avcp-input" value={jobId}
                    onChange={(e) => setJobId(e.target.value)}
                    data-testid={TID.bookWizardJob}>
              <option value="">Pick a job…</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.title} · {j.currency} · {fmt(j.starts_at)}</option>)}
            </select>
          </label>
          <label className="grid gap-1 text-sm">
            <span>Helper count requested</span>
            <input type="number" min="0" max="20" className="avcp-input"
                   value={helperCount}
                   onChange={(e) => setHelperCount(e.target.value)}
                   data-testid={TID.bookWizardHelperCount} />
          </label>
          <label className="grid gap-1 text-sm">
            <span>Message to lead (optional)</span>
            <textarea className="avcp-input" rows="3" value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      data-testid={TID.bookWizardMessage} />
          </label>

          {selectedJob && leadProfile && (
            <div className="space-y-3" data-testid={TID.bookWizardSummary}>
              <ClientBudgetCard record={selectedJob} />
              <div className="avcp-card p-4 bg-[var(--avcp-bg-2)]">
              <div className="text-sm font-medium mb-2">Platform rate estimate (not billed)</div>
              <ul className="text-sm space-y-1 font-mono">
                <li>Hours: {hours.toFixed(1)}</li>
                <li>Lead: {selectedJob.currency} {leadProfile.hourly_rate} × {hours.toFixed(1)} = {(leadProfile.hourly_rate * hours).toFixed(2)}</li>
                <li>Helpers ({helperCount}): {selectedJob.currency} {helperRate ?? "?"} × {hours.toFixed(1)} × {helperCount} = {((helperRate || 0) * hours * (Number(helperCount) || 0)).toFixed(2)}</li>
                <li className="font-bold">Total ≈ {selectedJob.currency} {total}</li>
              </ul>
              <p className="text-xs text-[var(--avcp-ink-soft)] mt-2">This estimate is separate from the client target budget. The helper rate uses the platform standard, and the lead can decline.</p>
              </div>
            </div>
          )}

          <button className="avcp-btn primary" disabled={busy || !jobId}
                  onClick={submit} data-testid={TID.bookWizardSubmit}>
            {busy ? "Sending…" : "Send booking request"}
          </button>
        </section>
      </div>
    </Layout>
  );
}
